var _a;
import { __decorate, __metadata, __param } from "tslib";
import { create } from '@patsnap/tahiti-core/utils/deferred';
import { PageSelectorServiceSymbol } from '@/presentation/components/page-selector/PageSelectorService';
import { Toast } from 'patsnap-biz';
import { useFormatRefiner } from '@/presentation/pages/detail/basic/hooks/useFormatRefiner';
import { useRefreshWithParams } from '@/presentation/pages/detail/basic/hooks/useRefreshWithParams';
import { copyViewDataApi, moveViewDataApi } from '@cores/apis/workspace_api/viewData';
import { ListOperationTypes } from '@/types/presentation/pages/detail/lockedTypes';
import { DetailServiceSymbol } from '@/services/DetailService';
import { injectable, inject, injectService } from '@/inversify.config';
import { i18nSymbol } from '@/i18n';
import _ from 'lodash';
import { FolderSettingService } from '@/domains/detail/folder/types/folder-setting-service.interface';
const noop = _.noop;
export const CopyMoveViewDataServiceSymbol = Symbol.for('CopyMoveViewDataService');
let CopyMoveViewDataService = class CopyMoveViewDataService {
    constructor(pageSelectorService, detailService, folderSettingService) {
        this.pageSelectorService = pageSelectorService;
        this.detailService = detailService;
        this.folderSettingService = folderSettingService;
        this._isDialogOpen = false;
        this._overWriteSelectedCount = undefined;
        this.mode = ListOperationTypes.OperationMode.COPY;
        this.isLoading = false;
    }
    get isDialogOpen() {
        return this._isDialogOpen;
    }
    get folderSelectionDefer() {
        return this._folderSelectionDefer;
    }
    get selectedCount() {
        return this._overWriteSelectedCount || this.pageSelectorService.selectedCount;
    }
    open({ mode, selectedCount }) {
        this._overWriteSelectedCount = selectedCount;
        this._folderSelectionDefer = create();
        if (!Object.values(ListOperationTypes.OperationMode).includes(mode)) {
            console.error('Invalid mode');
        }
        else {
            this.mode = mode;
        }
        this._isDialogOpen = true;
        return this._folderSelectionDefer.promise;
    }
    close() {
        this._isDialogOpen = false;
        this._overWriteSelectedCount = undefined;
        this.folderSelectionDefer.reject('Cancel');
    }
    onSubmit(params) {
        this.folderSelectionDefer.resolve({
            selectedFolderId: params.selectedFolderId,
            selectedSpaceId: params.selectedSpaceId,
        });
    }
    makeChange(params, hooks = { onStart: noop, onSuccess: noop, onError: noop }) {
        const mode = this.mode;
        const _hooks = {
            onStart: noop,
            onSuccess: noop,
            onError: noop,
            ...hooks,
        };
        const { spaceId, folderId, selectedFolderId, selectedSpaceId, } = params;
        const i18n = injectService(i18nSymbol);
        const copyMsg = i18n.t('workspace.viewData.copyViewDataTaskTip');
        const moveMsg = i18n.t('workspace.viewData.moveViewDataTaskTip');
        const option = {
            // 拼 URL
            space_id: spaceId,
            from_folder_id: folderId,
            to_folder_id: selectedFolderId,
            select_mode: this.pageSelectorService.mode,
            toSpaceId: selectedSpaceId,
            start: this.pageSelectorService.rangeFrom,
            end: this.pageSelectorService.rangeTo,
            sort: this.folderSettingService.folderSetting.current_sort,
            // post 参数
            doc_ids: this.pageSelectorService.finalIds,
            refiner: useFormatRefiner(),
            data_type: this.detailService.viewDataType,
            ...params.requestPayloadOverrides || {},
        };
        let requestApi;
        if (mode === 'copy')
            requestApi = copyViewDataApi;
        else if (mode === 'move')
            requestApi = moveViewDataApi;
        if (!requestApi) {
            throw new Error('Invalid mode');
        }
        // @ts-ignore
        Toast.closeAll();
        this.isLoading = true;
        _hooks.onStart();
        return requestApi(option)
            .then(() => {
            this.isLoading = false;
            _hooks.onSuccess();
            Toast({
                type: 'success',
                message: mode === 'copy' ? copyMsg : moveMsg,
                dangerouslyUseHTMLString: true,
                showClose: true,
                duration: 5000,
            });
        })
            .catch(err => {
            this.isLoading = false;
            _hooks.onError(err);
            Toast({
                dangerouslyUseHTMLString: true,
                message: err._error_msg,
                type: 'error',
                showClose: true,
            });
        });
    }
    onMovefinish() {
        if (this.mode === 'move') {
            useRefreshWithParams();
            this.pageSelectorService.unselectAll();
        }
    }
};
CopyMoveViewDataService = __decorate([
    injectable(),
    __param(0, inject(PageSelectorServiceSymbol)),
    __param(1, inject(DetailServiceSymbol)),
    __param(2, inject(FolderSettingService)),
    __metadata("design:paramtypes", [Object, Object, typeof (_a = typeof FolderSettingService !== "undefined" && FolderSettingService) === "function" ? _a : Object])
], CopyMoveViewDataService);
export { CopyMoveViewDataService };
